import * as React from "react";
import { SelectEditor } from "../../../../react-core/Html/SelectEditor";
import { CompanyViewClient } from "../../../../Wrap/Api/Company/CompanyView";
import { AccountAdviceType } from "../../../../Wrap/Constants/Enums/Apply/AccountAdviceType";
import { MifirDecisionMakerType } from "../../../../Wrap/Constants/Enums/Domain/MifirDecisionMakerType";
import { HierarchyLevelName } from "../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { UserClaim } from "../../../../Wrap/Constants/Enums/UserClaim";
import { HierarchyEntityMifirDecisionMaker } from "../../../../Wrap/Models/Domains/DecisionMaker/HierarchyEntityMifirDecisionMaker";
import { SectionHeader } from "../../../Dashboard/Components/Clients/NewBusinessWizard/Components/SharedComponents";
import { HierarchyIdHelper } from "../../Common/HierarchyIdHelper";
import { checkPermission } from "../../Permission/PermissionHelper";
import { IProcessComponentProps, ProcessComponent } from "../../Process/Process/ProcessComponent";
import { InvestmentSelectionAction } from "../Action/InvestmentSelectionAction";
import { leiCodevalidationMessage } from "../Resource/Content";

interface IDecisionMakerProps extends IProcessComponentProps {
    clAccountId: string;
    accountAdviceType: AccountAdviceType;
    companyCode: string;
    headAccountHierarchyId: string;
}

interface IDecisionMakerState {
    decisionMaker: HierarchyEntityMifirDecisionMaker[];
    leiCode: string;
}

export class DecisionMaker extends ProcessComponent<IDecisionMakerProps, IDecisionMakerState> {
    constructor(props: IDecisionMakerProps) {
        super(props);
        this.state = {
            decisionMaker: [],
            leiCode: null,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        const { accountAdviceType, companyCode, headAccountHierarchyId } = this.props;
        if (accountAdviceType == AccountAdviceType.Discretionary && companyCode != null) {
            new CompanyViewClient().get(companyCode).onSuccess((x) =>
                InvestmentSelectionAction.getDecisionMakers(
                    HierarchyIdHelper.toHierarchyIdString(HierarchyLevelName.Company, parseInt(x.CompanyId)),
                    (data) => {
                        this.setState({ decisionMaker: data, leiCode: x.LeiCode });
                    }
                )
            );
        } else if (accountAdviceType == AccountAdviceType.NonDiscretionary && headAccountHierarchyId != null) {
            InvestmentSelectionAction.getDecisionMakers(headAccountHierarchyId, (data) => {
                this.setState({ decisionMaker: data });
            });
        }
    }

    isValid() {
        if (this.props.accountAdviceType == AccountAdviceType.Discretionary && !this.state.leiCode) {
            this.props.showErrorMsgPopUp([leiCodevalidationMessage], true);
            return false;
        }
        return super.isValid();
    }

    private getName(id) {
        if (id == -1) {
            return "Account holder";
        }
        const { decisionMaker } = this.state;
        const obj = decisionMaker.find((ele) => ele.DecisionMakerId == id);
        if (!obj) {
            return "";
        }

        return obj.DecisionMakerType == MifirDecisionMakerType.OtherEntity
            ? obj.DecisionMakerExternalDetails.LeiCode
            : `${obj.DecisionMakerExternalDetails.GivenName} ${obj.DecisionMakerExternalDetails.Surname}`;
    }

    render() {
        const { data, onChange } = this.props;
        const { decisionMaker } = this.state;

        return (
            <>
                {SectionHeader("Decision Maker for this transaction")}
                <SelectEditor
                    cssWrapper="control-size-430 mb-30 rg-inline"
                    selected={data?.DecisionMakerId}
                    options={this.getDecisionMakerOptions(decisionMaker, this.props.accountAdviceType)}
                    placeholder="Please select"
                    onChange={(v) => {
                        onChange({ DecisionMakerId: v });
                    }}
                    required={"You need to select a decision maker for your request."}
                    getText={(o: any) => {
                        return this.getName(o);
                    }}
                />
                {this.props.clAccountId &&
                    (this.props.accountAdviceType == AccountAdviceType.Discretionary ? (
                        <p>
                            The decision maker field is used to capture the natural person making the decision to
                            acquire or dispose of a reportable financial instrument. Decision makers can be captured for
                            selection under the{" "}
                            {checkPermission(UserClaim.AdviserPortalDluMifirDecisionMakersCanView) ? (
                                <a
                                    href={
                                        "/clients/clientdetails/mifirdecisionmakers.aspx?claccountid=" +
                                        this.props.clAccountId
                                    }
                                    target="_blank"
                                >
                                    MIFIR Decision Makers
                                </a>
                            ) : (
                                "MIFIR Decision Makers"
                            )}
                            (URL for redirect to add natural person decision maker) page.
                        </p>
                    ) : (
                        <p>
                            The decision maker field is used to capture the natural person/entity making the decision to
                            acquire or dispose of a reportable financial instrument. If the decision maker is not
                            displayed in the dropdown, the decision maker must be captured under{" "}
                            {checkPermission(UserClaim.AdviserPortalDluMifirDecisionMakersCanView) ? (
                                <a
                                    href={
                                        "/clients/clientdetails/mifirdecisionmakers.aspx?claccountid=" +
                                        this.props.clAccountId
                                    }
                                    target="_blank"
                                >
                                    MIFIR Decision Makers
                                </a>
                            ) : (
                                "MIFIR Decision Makers"
                            )}
                            (URL redirect to add decision maker). The decision maker which has been entered will then be
                            available for selection when you return to this page.
                        </p>
                    ))}
            </>
        );
    }

    getDecisionMakerOptions(
        decisionMaker: HierarchyEntityMifirDecisionMaker[],
        accountAdviceType: AccountAdviceType
    ): number[] {
        if (accountAdviceType != AccountAdviceType.Discretionary) {
            return [-1, ...decisionMaker.map((ele) => ele.DecisionMakerId)];
        } else {
            return decisionMaker.map((ele) => ele.DecisionMakerId);
        }
    }
}
